import React, { Component } from 'react';
import { withRouter } from 'utilities/with_router';
import { useAuth } from 'auth_context';
import loading_animation from 'animations/tqps-loading.gif';
import submit_button_icon from 'icons/submit_login_white.png';
import login_spinner_icon from 'icons/submit_login_spinner.png';

class ResourceLinkPage extends Component {
	constructor(props) {
		super(props);

		this.state = {
			status: 'loading', // loading | requires_login | ready | not_found
			resource: null,
			username: '',
			password: '',
			failed_login: false,
			submit_button: submit_button_icon,
			submit_button_animation: '',
		};
	}

	componentDidMount() {
		this.resolveLink();
	}

	// Resolve once the image is fully cached (or after a safety timeout / on error),
	// so the splash never renders with a half-loaded thumbnail.
	preloadThumbnail = thumbnail =>
		new Promise(resolve => {
			if (!thumbnail) {
				resolve();
				return;
			}
			const img = new Image();
			img.onload = resolve;
			img.onerror = resolve;
			img.src = thumbnail;
			setTimeout(resolve, 6000);
		});

	resolveLink = async () => {
		const recordId = this.props.params?.recordId;

		try {
			const response = await this.props.auth.authFetch(`/api/resource_link/${recordId}/`);

			if (!response.ok) {
				this.setState({ status: 'not_found' });
				return;
			}

			const data = await response.json();

			if (data.available) {
				await this.preloadThumbnail(data.thumbnail);
				this.setState({ status: 'ready', resource: data });
			} else if (data.requires_login) {
				await this.preloadThumbnail(data.thumbnail);
				this.setState({ status: 'requires_login', resource: data });
			} else {
				this.setState({ status: 'not_found' });
			}
		} catch {
			this.setState({ status: 'not_found' });
		}
	};

	animateLogin = () => {
		this.setState({
			submit_button: login_spinner_icon,
			submit_button_animation: 'spin-animation',
		});
	};

	removeLoginAnimation = () => {
		this.setState({
			submit_button: submit_button_icon,
			submit_button_animation: '',
		});
	};

	loginUser = async () => {
		this.animateLogin();
		this.setState({ failed_login: false });

		try {
			const data = await this.props.auth.login(this.state.username, this.state.password);

			this.removeLoginAnimation();

			if (data['authenticated']) {
				// Now authenticated — re-resolve the link to get the download URL.
				this.setState({ status: 'loading' }, this.resolveLink);
			} else {
				this.setState({ failed_login: true });
			}
		} catch {
			this.removeLoginAnimation();
			this.setState({ failed_login: true });
		}
	};

	handleKeyDown = event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.loginUser();
		}
	};

	goToHub = (param, value) => {
		this.props.navigate(`/resources?${param}=${encodeURIComponent(value)}`);
	};

	renderMetaRow = (label, value) => {
		if (!value || (Array.isArray(value) && value.length === 0)) return null;
		return (
			<div className="resource-story-meta-row">
				<span className="resource-story-meta-label">{label}</span>
				<span className="resource-story-meta-value">
					{Array.isArray(value) ? value.join(', ') : value}
				</span>
			</div>
		);
	};

	renderStory = () => {
		const r = this.state.resource;
		const locations = [...(r.us_states || []), ...(r.countries || [])];
		const has_warning = r.content_warning && r.content_warning.toUpperCase() !== 'SFW';
		const action_url = r.download_url || r.link_url;
		const action_label = r.download_url ? 'Download' : 'Open Resource';

		return (
			<div className="resource-story">
				<div className="resource-story-left">
					{r.thumbnail && <img className="resource-link-thumb" src={r.thumbnail} alt="" />}
					{action_url && (
						<a
							className="resource-link-download-btn"
							href={action_url}
							target="_blank"
							rel="noopener noreferrer"
						>
							{action_label}
						</a>
					)}
					<span className={`resource-story-badge resource-story-badge--${r.public ? 'public' : 'private'}`}>
						{r.public ? 'Public' : 'Private'} Resource
					</span>
				</div>

				<div className="resource-story-right">
					<h1 className="resource-story-title">{r.title}</h1>

					{has_warning && (
						<div className="resource-story-warning">
							⚠ Content warning: {r.content_warning}
						</div>
					)}

					<div className="resource-story-meta">
						{this.renderMetaRow('Published by', r.author)}
						{this.renderMetaRow('Year', r.year)}
						{this.renderMetaRow('Language', r.languages)}
						{this.renderMetaRow('Relevant to', r.relevant_to)}
						{this.renderMetaRow('Location', locations)}
					</div>

					{r.blurb && <p className="resource-story-blurb">{r.blurb}</p>}

					{r.notes && <p className="resource-story-notes">{r.notes}</p>}

					{(r.category || (r.tags || []).length > 0) && (
						<div className="resource-story-chips">
							{r.category && (
								<button
									className="resource-story-chip resource-story-chip--category"
									onClick={() => this.goToHub('category', r.category)}
								>
									{r.category}
								</button>
							)}
							{(r.tags || []).map(tag => (
								<button
									key={tag}
									className="resource-story-chip"
									onClick={() => this.goToHub('tag', tag)}
								>
									{tag}
								</button>
							))}
						</div>
					)}

					<div className="resource-story-footer">
						This resource is part of the Constellation Network{' '}
						<a
							href="/resources"
							onClick={e => {
								e.preventDefault();
								this.props.navigate('/resources');
							}}
						>
							Resource Hub →
						</a>
					</div>
				</div>
			</div>
		);
	};

	render() {
		const { status, resource } = this.state;

		if (status === 'loading') {
			return (
				<div className="resource-link-splash">
					<img className="loading-animation" src={loading_animation} alt="" />
					<h3>Fetching your resource…</h3>
				</div>
			);
		}

		if (status === 'ready') {
			return this.renderStory();
		}

		if (status === 'not_found') {
			return (
				<div className="resource-link-splash">
					<h2>Resource not found</h2>
					<h5>This resource link doesn't exist or is no longer available.</h5>
				</div>
			);
		}

		// requires_login
		return (
			<div className="resource-link-splash">
				{resource?.thumbnail && <img className="resource-link-thumb" src={resource.thumbnail} alt="" />}
				{resource?.title && <h2>{resource.title}</h2>}
				<h4>
					You've been shared a private resource from the Constellation Network.
					<br />
					Log in to continue.
				</h4>

				<form
					className="resource-link-login"
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '12px',
					}}
					onSubmit={e => {
						e.preventDefault();
						this.loginUser();
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
						<img className="submit-button" />
						<input
							type="text"
							value={this.state.username}
							onChange={e => this.setState({ username: e.target.value })}
							className="login-fields"
							placeholder="username"
							autoComplete="username"
							onKeyDown={this.handleKeyDown}
						/>
						<img className="submit-button" />
					</div>

					<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
						<img className="submit-button" />
						<input
							type="password"
							value={this.state.password}
							onChange={e => this.setState({ password: e.target.value })}
							className="login-fields"
							placeholder="password"
							autoComplete="current-password"
							onKeyDown={this.handleKeyDown}
						/>
						<img
							className={`submit-button ${this.state.submit_button_animation}`}
							onClick={this.loginUser}
							src={this.state.submit_button}
						/>
					</div>

					<div
						style={{
							display: this.state.failed_login ? '' : 'none',
							animation: 'fade-in 1s forwards',
						}}
						className="failed-login-alert"
					>
						<p>Incorrect login.</p>
					</div>
				</form>
			</div>
		);
	}
}

const ResourceLink = props => {
	const auth = useAuth();
	return <ResourceLinkPage {...props} auth={auth} />;
};

export default withRouter(ResourceLink);
